import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloudDownloadOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { HoopsViewer, HoopsViewerLoader } from '@xometry/hoops-viewer';
import { amplitudeLogger } from 'common/amplitude';
import { PREVIEW_FILES_EVENTS } from 'common/enums/amplitudeEvents';
import { useCurrentPageForAmplitude } from 'common/hooks/useCurrentPageForAmplitude';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { Modal } from 'components/UI/Modal';
import PdfViewer from 'components/UI/Modal/PdfViewer/PdfViewer';
import { PreviewFile } from 'components/UI/PreviewImage/PreviewImage';
import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import styles from './PreviewModal.module.less';

interface IProps {
  onClose: () => void;
  initFileId?: number;
  files: PreviewFile[];
  sourceId?: number | string;
  sourceType?: 'offer' | 'order' | 'rfqOffer';
  showTitle?: boolean;
}

export const PreviewModal: FC<IProps> = ({ initFileId, files, onClose, sourceId, sourceType, showTitle = true }) => {
  const [currentFile, setCurrentFile] = useState<PreviewFile | undefined>(files[0]);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const { t } = useTranslation();
  const currentPage = useCurrentPageForAmplitude();

  const handleNext = () => {
    if (!files[currentIndex + 1]) {
      return;
    }

    setCurrentFile(files[currentIndex + 1]);
    setRotation(0);
    setScale(1);
  };

  const handlePrev = () => {
    if (!files[currentIndex - 1]) {
      return;
    }

    setCurrentFile(files[currentIndex - 1]);
    setRotation(0);
    setScale(1);
  };

  const downHandler = ({ key }: { key: string }) => {
    if (key === 'ArrowRight') {
      handleNext();
    }

    if (key === 'ArrowLeft') {
      handlePrev();
    }
  };

  useEffect(() => {
    setCurrentFile(files.find((file) => file.id === initFileId) || files[0]);
  }, [initFileId, files]);

  useEffect(() => {
    window.addEventListener('keydown', downHandler);

    return () => {
      window.removeEventListener('keydown', downHandler);
    };
  });

  const escHandler = (event: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (event.data.event_id === 'iframe_keydown' && event.data.code === 'Escape') {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener('message', escHandler);

    return () => {
      window.removeEventListener('message', escHandler);
    };
  });

  if (!currentFile) {
    return null;
  }

  const fileName = currentFile?.name;
  const ext = fileName ? fileName.split('.').pop()?.toLowerCase() : '';

  const isSCS = ext === 'scs';
  const isPDF = ext === 'pdf';

  const currentIndex = files.indexOf(currentFile);

  const titleElement = (
    <div className={styles.title}>
      <div className={styles.titleControls}>
        <AntButton type="link" onClick={handlePrev} disabled={!files[currentIndex - 1]} noPadding>
          <ArrowLeftOutlined />
        </AntButton>
        {t('commonPhrases.files')}&nbsp;
        {currentIndex + 1}/{files.length}
        <AntButton type="link" onClick={handleNext} disabled={!files[currentIndex + 1]} noPadding>
          <ArrowRightOutlined />
        </AntButton>
      </div>
      <div className={styles.titleControls}>
        <AntButton
          type="link"
          download
          target="_blank"
          href={currentFile.downloadUrl}
          onClick={() => {
            amplitudeLogger(PREVIEW_FILES_EVENTS.PREVIEW_FILE_DOWNLOADED, {
              file_id: currentFile?.id,
              source_id: sourceId,
              source_type: sourceType,
              current_page: currentPage,
              position: currentIndex,
            });
          }}
          className={styles.downloadBtn}
          noPadding
        >
          {currentFile.name}
          &nbsp;
          <CloudDownloadOutlined />
        </AntButton>
      </div>
      {!isSCS && !isPDF && (
        <div className={styles.titleControls}>
          <AntButton
            type="link"
            onClick={() => setScale(scale === 1.5 ? 1 : 0.5)}
            disabled={isSCS || scale === 0.5}
            noPadding
          >
            <ZoomOutOutlined />
          </AntButton>
          <AntButton
            type="link"
            onClick={() => setScale(scale === 0.5 ? 1 : 1.5)}
            disabled={isSCS || scale === 1.5}
            noPadding
          >
            <ZoomInOutlined />
          </AntButton>
          <AntButton type="link" onClick={() => setRotation(rotation + 90)} disabled={isSCS} noPadding>
            <RotateRightOutlined />
          </AntButton>
          <AntButton type="link" onClick={() => setRotation(rotation - 90)} disabled={isSCS} noPadding>
            <RotateLeftOutlined />
          </AntButton>
        </div>
      )}
    </div>
  );
  const modalTitle = (
    <div className={styles.modalTitle}>
      <Trans t={t} i18nKey={'previewModal.warning'}>
        <span>Please download the files and use them for production.</span>
        <span>DO NOT use the Preview file for production!</span>
      </Trans>
    </div>
  );

  return (
    <Modal
      open
      width="1024px"
      footer={false}
      onCancel={() => onClose()}
      className={styles.modalPreview}
      title={showTitle ? modalTitle : null}
    >
      {titleElement}
      {isSCS && currentFile.largeUrl ? (
        <HoopsViewerLoader>
          <HoopsViewer url={currentFile.largeUrl} originalFilename={currentFile.name} />
        </HoopsViewerLoader>
      ) : null}
      {isPDF && currentFile.largeUrl ? <PdfViewer src={currentFile.largeUrl} /> : null}
      {!isSCS && !isPDF && currentFile.largeUrl ? (
        <div className="ant-image">
          <img
            src={currentFile.largeUrl}
            alt={currentFile.name}
            style={{ transform: `rotate(${rotation}deg) scale(${scale})` }}
          />
        </div>
      ) : null}
    </Modal>
  );
};
